import modelExtend from 'dva-model-extend';
import deepmerge from 'deepmerge';
import personModel from './person';
import { PersonProps } from '../components/shared/Person/Person';
import { AppAction, Field } from '../declarations/widget-ums';
import addressModel from './address';
import { EMPTY_FIELD } from '../constants/emptyField';
import applicantSerializer from '../helpers/applicantDataSerializer';
import { SEX } from '../components/shared/PersonGender/PersonGender';
import { hasErrors } from '../validators';
import { ADDRESS_MATCH } from '../constants/spouse';
import { DOCUMENT_FIELD } from '../constants/document';
import { SPOUSE_FIELD_NAME } from '../constants/fieldNames';
import { SPOUSE } from '../constants/modelNames';

export interface SpouseState extends PersonProps {
  addressMatchWithApplicant: Field<ADDRESS_MATCH | null>;
  isRegisteredInMoscow: boolean;
}

const initialSpouseState: Partial<SpouseState> = {
  addressMatchWithApplicant: { ...EMPTY_FIELD, value: null },
  isRegisteredInMoscow: true,
};

const spouseModel = modelExtend(personModel, addressModel, {
  namespace: SPOUSE,
  state: initialSpouseState,
  reducers: {
    reset(state: SpouseState) {
      return Object.assign(spouseModel.state, { sex: state.sex });
    },
    'address/toggle/isRegisteredInMoscow'(
      state: SpouseState,
      { payload }: AppAction<boolean>
    ) {
      state.isRegisteredInMoscow = payload;
      return state;
    },
    'address/change/matchWithApplicant'(
      state: SpouseState,
      { payload }: AppAction<ADDRESS_MATCH | null>
    ) {
      return Object.assign(state, payload);
    },
    'set/error'(state, { payload }) {
      return deepmerge(state, payload);
    },
    'reset/error/birthdate'(state) {
      return deepmerge(state, { birthdate: { error: false } });
    },
  },
  effects: {
    *'address/change/matchWithApplicant'({ payload }, { put }) {
      yield put({ type: 'address/reset' });
      yield put({
        type: 'address/toggle/isRegisteredInMoscow',
        payload: true,
      });
      yield put({ type: 'form/saveDraft' });
      if (
        payload[SPOUSE_FIELD_NAME.addressMatchWithApplicant]?.value ===
        ADDRESS_MATCH.match
      ) {
        yield put({
          type: 'children/check/update/addressMatchWithApplicant',
        });
      }
    },
    *'address/toggle/isRegisteredInMoscow'({ payload }, { put }) {
      yield put({ type: 'address/reset' });
      yield put({ type: 'children/toggle/isRegisteredInMoscow', payload });
    },
    elkWatcher: [
      function* ({ take, put }) {
        const { payload } = yield take('elk/getBlocks/finish');
        const { sex } = applicantSerializer(payload);
        yield put({
          type: 'onChange',
          payload: {
            sex: Object.assign({}, sex, {
              value: sex.value === SEX.MALE ? SEX.FEMALE : SEX.MALE,
            }),
          },
        });
      },
      { type: 'watcher' },
    ],
    formErrorWatcher: [
      function* ({ take, put }) {
        while (true) {
          const {
            payload: { spouse },
          } = yield take('form/set/error');
          const spouseHasErrors = hasErrors(spouse);

          if (spouseHasErrors) {
            yield put({ type: 'set/error', payload: spouse });
          }
        }
      },
      { type: 'watcher' },
    ],
    draftWatcher: [
      function* ({ take, put }) {
        while (true) {
          const {
            payload: { spouse },
          } = yield take('form/set/draftData');
          if (spouse) {
            const { document, address, ...person } = spouse;
            yield put({ type: 'setFromDraft', payload: person });
            yield put({
              type: 'document/set',
              payload: document,
              documentField: DOCUMENT_FIELD.dul,
            });
            yield put({
              type: 'address/set/draftData',
              payload: address,
            });
          }
          yield put({
            type: 'form/set/readyNamespace',
            payload: SPOUSE,
          });
          yield put({
            type: `${SPOUSE}/passport/validity`,
          });
        }
      },
      { type: 'watcher' },
    ],
  },
});

export default spouseModel;
